import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await fetch(`/api/jobs/${slug}`);
        if (!response.ok) throw new Error("Job not found");
        const data = await response.json();
        setJob(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center pt-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate("/showcase")}
            className="text-cyan-400 hover:text-white transition mb-4"
          >
            ← Back to Showcase
          </button>
          <div className="bg-red-900 bg-opacity-50 p-8 rounded-lg">
            <p className="text-red-200">Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate("/showcase")}
            className="text-cyan-400 hover:text-white transition mb-4"
          >
            ← Back to Showcase
          </button>
          <p>Project not found</p>
        </div>
      </div>
    );
  }

  const images = job.images && Array.isArray(job.images) ? job.images : [];
  const currentImage = images[currentImageIndex];

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <Link
          to="/showcase"
          className="text-cyan-400 hover:text-white transition mb-6 inline-block"
        >
          ← Back to Showcase
        </Link>

        {/* Project Title */}
        <h1 className="text-5xl font-bold mb-2">{job.title}</h1>
        <p className="text-gray-300 mb-8">
          Completed: {formatDate(job.completion_date)}
        </p>

        {/* Content Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2">
            {/* Image Carousel */}
            {images.length > 0 ? (
              <div className="mb-8">
                <div className="relative bg-gray-800 rounded-lg overflow-hidden mb-4">
                  <img
                    src={currentImage.image_path}
                    alt={currentImage.image_description || "Project image"}
                    className="w-full h-96 object-cover"
                    onError={(e) => {
                      e.target.src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23333' width='400' height='300'/%3E%3Ctext x='50%' y='50%' text-anchor='middle' dy='.3em' fill='%23999' font-family='sans-serif' font-size='18'%3EImage Not Found%3C/text%3E%3C/svg%3E";
                    }}
                  />

                  {/* Carousel Controls */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={goToPrevious}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition"
                      >
                        ❮
                      </button>
                      <button
                        onClick={goToNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition"
                      >
                        ❯
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 px-4 py-2 rounded-full text-sm">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    </>
                  )}
                </div>

                {/* Image Description */}
                {currentImage.image_description && (
                  <p className="text-gray-300 text-sm italic mb-4">
                    {currentImage.image_description}
                  </p>
                )}

                {/* Thumbnail Strip */}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition ${
                          idx === currentImageIndex
                            ? "border-cyan-400"
                            : "border-gray-600 hover:border-gray-400"
                        }`}
                      >
                        <img
                          src={`/uploads/${img.image_path}`}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect fill='%23444' width='80' height='80'/%3E%3C/svg%3E";
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-12 text-center mb-8">
                <p className="text-gray-400">
                  No images available for this project
                </p>
              </div>
            )}

            {/* Description */}
            <div className="bg-gray-800 bg-opacity-50 rounded-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-cyan-400">
                About This Project
              </h2>
              <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                {job.description}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div className="bg-gray-800 bg-opacity-50 rounded-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold mb-4">Project Details</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-400 text-sm">Completed Date</p>
                  <p className="text-white font-semibold">
                    {formatDate(job.completion_date)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total Images</p>
                  <p className="text-white font-semibold">{images.length}</p>
                </div>
                {images.length > 0 && (
                  <div>
                    <p className="text-gray-400 text-sm">Viewing Image</p>
                    <p className="text-white font-semibold">
                      {currentImageIndex + 1} of {images.length}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobDetail;
